import { useEffect, useMemo, useState } from "react";
import { loadSession } from "../auth";
import Button from "../components/Button";
import Card from "../components/Card";
import { Link } from "react-router-dom";
import { listMyAssignments, getOrgAdminDashboardStats, listWorksites } from "../services/api";
import { shouldBlockFeatureLoads } from "../utils/onboardingGate";
import { useAdminScope } from "../hooks/useAdminScope";
import { useTranslation } from "react-i18next";

function StatCard({ label, value, loading, colorClass = "border-l-slate-300" }) {
  return (
    <Card className={`border-l-4 ${colorClass}`}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-3xl font-bold text-slate-900">{loading ? "…" : value}</div>
    </Card>
  );
}

function OrgAdminDashboard({ user, t, i18n }) {
  const isWorksiteScoped = user?.worksite_id !== null && user?.worksite_id !== undefined;

  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsErr, setStatsErr] = useState("");

  const { worksiteId, setWorksiteId } = useAdminScope(user?.id);

  const [worksites, setWorksites] = useState([]);

  // Load worksite list for tenant-wide admins
  useEffect(() => {
    if (isWorksiteScoped) return;
    let alive = true;
    listWorksites()
      .then((res) => alive && setWorksites(Array.isArray(res?.items) ? res.items : []))
      .catch(() => {});
    return () => { alive = false; };
  }, [isWorksiteScoped]);

  // Validate saved worksite against loaded list
  useEffect(() => {
    if (!worksiteId || worksites.length === 0) return;
    const ids = worksites.map((w) => String(w.id ?? ""));
    if (!ids.includes(String(worksiteId))) setWorksiteId("");
  }, [worksites, worksiteId, setWorksiteId]);

  // Load stats whenever worksiteId changes
  useEffect(() => {
    let alive = true;
    setStatsLoading(true);
    setStatsErr("");
    getOrgAdminDashboardStats({ worksiteId: isWorksiteScoped ? "" : worksiteId })
      .then((res) => alive && setStatsData(res))
      .catch((e) => alive && setStatsErr(e?.message || t("dashboard.error_stats")))
      .finally(() => alive && setStatsLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksiteId, isWorksiteScoped]);

  const tenantName = statsData?.tenant?.name || user?.tenantId || "";
  const stats = statsData?.stats || { total: 0, completed: 0, in_progress: 0, not_started: 0 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t("dashboard.welcome_org_admin", { tenantName })}
        </h1>
        <p className="text-slate-500 mt-1">{t("dashboard.subtitle_org_admin")}</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Link to="/admin/users">
          <Button>{t("dashboard.btn_view_employees")}</Button>
        </Link>

        {!isWorksiteScoped && worksites.length > 0 && (
          <select
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            value={worksiteId}
            onChange={(e) => setWorksiteId(e.target.value)}
          >
            <option value="">{t("dashboard.worksite_filter_all")}</option>
            {worksites.map((ws) => (
              <option key={ws.id} value={ws.id}>{ws.name}</option>
            ))}
          </select>
        )}
      </div>

      {statsErr ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{statsErr}</div>
      ) : (
        <div className="grid sm:grid-cols-4 gap-4">
          <StatCard label={t("dashboard.stats_total")}       value={stats.total}       loading={statsLoading} colorClass="border-l-blue-500" />
          <StatCard label={t("dashboard.stats_completed")}   value={stats.completed}   loading={statsLoading} colorClass="border-l-emerald-500" />
          <StatCard label={t("dashboard.stats_in_progress")} value={stats.in_progress} loading={statsLoading} colorClass="border-l-amber-500" />
          <StatCard label={t("dashboard.stats_not_started")} value={stats.not_started} loading={statsLoading} colorClass="border-l-slate-400" />
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const session = loadSession();
  const { t, i18n } = useTranslation();
  const user = session?.user;

  const isOrgAdmin =
    user?.derived?.isOrgAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("org_super_admin"));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isOrgAdmin) return; // org admins use OrgAdminDashboard, not personal assignments
    let alive = true;
    setLoading(true);
    setErr("");

    // Block normal feature loads while onboarding/tenant gate is active
    if (shouldBlockFeatureLoads()) {
      setItems([]);
      setLoading(false);
      return () => { alive = false; };
    }

    listMyAssignments()
      .then((res) => alive && setItems(Array.isArray(res) ? res : []))
      .catch((e) => alive && setErr(e?.message || t("dashboard.error_generic")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgAdmin]);

  const stats = useMemo(() => {
    const s = { total: 0, due: 0, inProgress: 0, completed: 0 };
    for (const it of items) {
      s.total += 1;
      if (it.status === "completed") s.completed += 1;
      else if (
        it.status === "in-progress" ||
        it.status === "passed-quiz" ||
        it.status === "awaiting-upload"
      )
        s.inProgress += 1;
      else s.due += 1;
    }
    return s;
  }, [items]);

  const nextUp = useMemo(() => {
    const order = {
      "not-started": 0,
      "in-progress": 1,
      "passed-quiz": 2,
      "awaiting-upload": 3,
      "completed": 9
    };
    return [...items]
      .sort((a, b) => {
        const sa = order[a?.status] ?? 99;
        const sb = order[b?.status] ?? 99;
        if (sa !== sb) return sa - sb;
        const da = a?.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const db = b?.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        if (da !== db) return da - db;
        return (a?.title || "").localeCompare(b?.title || "");
      })
      .slice(0, 3);
  }, [items]);

  if (isOrgAdmin) {
    return <OrgAdminDashboard user={user} t={t} i18n={i18n} />;
  }

  const name = user?.name;
  const welcomeText = name ? t("dashboard.welcome_name", { name }) : t("dashboard.welcome");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{welcomeText}</h1>
        <p className="text-slate-500 mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Link to="/trainings">
          <Button>{t("dashboard.btn_my_trainings")}</Button>
        </Link>
        {user?.derived?.isManager && (
          <Link to="/team">
            <Button variant="secondary">{t("dashboard.btn_my_team")}</Button>
          </Link>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard label={t("dashboard.stats_due")}         value={stats.due}        loading={loading} colorClass="border-l-red-400" />
        <StatCard label={t("dashboard.stats_in_progress")} value={stats.inProgress} loading={loading} colorClass="border-l-amber-500" />
        <StatCard label={t("dashboard.stats_completed")}   value={stats.completed}  loading={loading} colorClass="border-l-emerald-500" />
      </div>

      <Card title={t("dashboard.next_up_title")}>
        {loading ? (
          <div className="text-sm text-slate-500">{t("dashboard.loading")}</div>
        ) : err ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>
        ) : nextUp.length ? (
          <ul className="divide-y divide-slate-100">
            {nextUp.map((it) => (
              <li key={it.trainingId || it.slug} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{it.title}</div>
                  {it.dueDate && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t("dashboard.due_on", {
                        date: new Date(it.dueDate).toLocaleDateString(i18n.language)
                      })}
                    </div>
                  )}
                </div>
                <Link to={`/training/${it.trainingId || it.slug}`}>
                  <Button size="sm">{t("dashboard.open")}</Button>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-500 py-4 text-center">{t("dashboard.caught_up")}</div>
        )}
      </Card>
    </div>
  );
}
